import { NumberSentType } from '@/interfaces';
import { convertStringArrayToEnumLikeObject } from '@/utils';
import dayjs, { Dayjs } from 'dayjs';

export const dateRange = convertStringArrayToEnumLikeObject([
  'pastWeek',
  'pastMonth',
  'pastYear',
  'customRange',
] as const);

export type DateRange = keyof typeof dateRange;

export const isDateRange = (value: string | null): value is DateRange =>
  !!value && value in dateRange;

export const dashboardTab = {
  globalOverview: 'global-overview',
  productOperations: 'product-operations',
} as const;

// Shared display date format for the dashboard tabs — update here to change
// every displayed date/datetime at once.
export const DASHBOARD_DATE_FORMAT = 'DD/MM/YYYY';
export const DASHBOARD_DATETIME_FORMAT = `${DASHBOARD_DATE_FORMAT} HH:mm:ss`;

export type DashboardTab = (typeof dashboardTab)[keyof typeof dashboardTab];

// Sync/Export buttons live in NewDashboard (same row as the tab selector,
// end of row) but the data/handlers they trigger live inside whichever tab
// is active — each tab reports its current handlers/state up through this.
export interface DashboardTabActions {
  onSync: () => void;
  onExport: () => void;
  isSyncing: boolean;
  canExport: boolean;
}
import dayOfYear from 'dayjs/plugin/dayOfYear';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekday from 'dayjs/plugin/weekday';
dayjs.extend(weekOfYear);
dayjs.extend(dayOfYear);
dayjs.extend(weekday);

export const formatDay = (day: dayjs.Dayjs) => {
  return day.format('D.M');
};

export const getStartOfMonth = () => {
  const month = dayjs().month();
  const lastMonth = dayjs().month(month - 1);
  return lastMonth.startOf('month');
};

export const getEndOfMonth = () => {
  const month = dayjs().month();
  const lastMonth = dayjs().month(month - 1);
  return lastMonth.endOf('month');
};

export const getDaysInMonthRange = () => {
  const startDate = getStartOfMonth();
  const endDate = getEndOfMonth();
  const result = [formatDay(startDate)];
  let index = 1;
  for (
    let day = startDate.add(1, 'day');
    day.isBefore(endDate);
    day = day.add(1, 'day')
  ) {
    index++;
    if (index % 5 === 0 && index <= 25) {
      result.push(formatDay(day));
    } else {
      result.push('');
    }
  }
  result.splice(result.length - 1, 1, formatDay(endDate));
  return result;
};

export const getStartOfWeek = () => {
  return dayjs().weekday(-7);
};

export const getEndOfWeek = () => {
  return dayjs().weekday(-1);
};

export const getDaysInWeekRange = () => {
  const startDate = getStartOfWeek();
  const endDate = getEndOfWeek();
  const result = [];
  let newDate = startDate;
  while (newDate.isBefore(endDate)) {
    result.push(formatDay(newDate));
    newDate = newDate.add(1, 'day');
  }
  return result;
};

export const formatDateTime = (date: Dayjs, sep: string) => {
  switch (sep) {
    case 'month':
      return date.format('MMM');
    case 'year':
      return date.format('YYYY');
    default:
      return date.format('D.M');
  }
};

const _formatDateFromPartOfDate = (
  date: number,
  sep: 'day' | 'month' | 'year',
  baseDate: Dayjs, // Using Dayjs for base date manipulation -> To prevent year has 29/02
): Date => {
  switch (sep) {
    case 'month':
      return new Date(baseDate.year(), date - 1, 1); // Month is zero-based (0 = January)
    case 'year':
      return new Date(date, 0, 1); // Month is zero-based (0 = January)
    default:
      return new Date(baseDate.year(), baseDate.month(), date);
  }
};
export const mappingReportDataDashboard = (chartData: NumberSentType[]) => {
  if (chartData) {
    const { length } = chartData;

    if (length <= 13) {
      return chartData.map(item => item.time);
    } else {
      return chartData.reduce((acc: string[], item, index) => {
        if ((index % 5 === 0 && index !== length - 2) || index === length - 1) {
          acc.push(item.time);
        } else {
          acc.push('');
        }
        return acc;
      }, []);
    }
  }
};

export const mappingDateDataDashboard = (
  sep: 'day' | 'month' | 'year',
  startDate: Dayjs,
  endDate: Dayjs,
) => {
  let newDate = startDate;
  let newEndDate = endDate.add(1, sep);
  const result: string[] = [];
  let newGetUnit: 'date' | 'month' | 'year' = sep === 'day' ? 'date' : sep;
  const isNeedToBeautify =
    endDate.get(newGetUnit) - startDate.get(newGetUnit) > 12;

  if (isNeedToBeautify) {
    let index = 1;

    while (newDate.isBefore(newEndDate, sep)) {
      if (
        ((index === 1 || index % 5 === 0) && index <= 25) ||
        newDate.isSame(endDate, sep)
      ) {
        result.push(formatDateTime(newDate, sep));
      } else {
        result.push('');
      }
      index++;
      newDate = newDate.add(1, sep);
    }
  } else {
    while (newDate.isBefore(newEndDate, sep)) {
      result.push(formatDateTime(newDate, sep));
      newDate = newDate.add(1, sep);
    }
  }

  return result;
};

const processCustomDatePicker = (
  startDate: string,
  endDate: string,
): 'month' | 'year' | 'day' => {
  const currentListYear: string[] = [];
  const currentListMonth: string[] = [];

  for (
    let i = new Date(startDate).getFullYear();
    i <= new Date(endDate).getFullYear();
    i++
  ) {
    currentListYear.push(i.toString());

    if (currentListYear.length > 1) {
      return 'year';
    }
  }

  for (
    let i = new Date(startDate).getMonth();
    i <= new Date(endDate).getMonth();
    i++
  ) {
    currentListMonth.push(i.toString());

    if (currentListMonth.length > 1) {
      return 'month';
    }
  }

  return 'day';
};

export const processGroupOfDateTrucSequence = (
  picker: string,
  startDate: string,
  endDate: string,
): 'month' | 'year' | 'day' => {
  switch (picker) {
    case 'year':
      return 'month';

    case 'day':
      return processCustomDatePicker(startDate, endDate);

    default:
      return 'day';
  }
};
